import { completeSignin } from './signin-analytics.js';
import { rememberOfflineAuthUser } from './offline-account.js';
import { updateBrowserProductAccess } from './browser-product-access.js';
import { fetchWithNetworkRetry } from './network-request.js';
import { isKnownCrawlerUserAgent } from './runtime-error-filter.js';
import type { MapAnalyticsTrack } from '../shared/analytics-schema.js';
import { AUTH_STATE_READ_RETRY } from './network-retry-policies.js';
import { setInternalAnalyticsExclusion } from './analytics-identity.js';
import { isObject } from '../shared/runtime-values.js';
import { clearCustomerAnalytics, refreshCustomerAnalytics } from './customer-analytics-context.js';

type Track = MapAnalyticsTrack;

export type AuthStateResult<User> =
  | { status: 'signed_in'; user: User }
  | { status: 'signed_out' }
  | { status: 'failed'; reason: 'network_error' | 'http_error' | 'invalid_response' | 'response_read_error' };

interface FetchAuthStateOptions {
  track: Track;
  onOutcome?: (outcome: 'completed' | 'failed') => void;
  userAgent?: string;
  fetchImpl?: typeof fetch;
  retryDelayMs?: number;
  analyticsStorage?: Parameters<typeof setInternalAnalyticsExclusion>[1];
  isCurrent?: () => boolean;
}

function onlineState(): string {
  return 'navigator' in globalThis && navigator.onLine === false ? 'offline' : 'online';
}

function responseContentType(response: Response): string {
  const value = response.headers.get('content-type')?.toLowerCase() ?? '';
  if (!value) return 'missing';
  if (value.includes('json')) return 'json';
  if (value.includes('html')) return 'html';
  if (value.startsWith('text/')) return 'text';
  return 'other';
}

interface AuthFailureDetails {
  kind: string;
  reason: string;
  error_name: string;
  error_message: string;
  http_status?: string;
  content_type?: string;
}

function errorBlob(reason: string, cause: unknown, httpStatus?: number, contentType?: string): string {
  const name = cause instanceof Error ? cause.name : 'Error';
  const message = cause instanceof Error ? cause.message : String(cause);
  const details: AuthFailureDetails = {
    kind: 'auth_state_fetch_failed',
    reason,
    error_name: name,
    error_message: message,
  };
  if (httpStatus) details.http_status = String(httpStatus);
  if (contentType) details.content_type = contentType;
  return JSON.stringify(details);
}

async function readAccountResponse(response: Response) {
  const raw: unknown = await response.json();
  if (raw === null || !isObject(raw) || Array.isArray(raw) || !('user' in raw)) throw new SyntaxError('Invalid account response');
  if (raw.user !== null && (!isObject(raw.user) || Array.isArray(raw.user))) throw new SyntaxError('Invalid account response');
  return raw;
}

export async function fetchAuthState<User = unknown>({
  track,
  onOutcome,
  userAgent = !('navigator' in globalThis) ? '' : navigator.userAgent,
  fetchImpl,
  retryDelayMs,
  analyticsStorage,
  isCurrent = () => true,
}: FetchAuthStateOptions): Promise<AuthStateResult<User>> {
  clearCustomerAnalytics();
  let response: Response;
  try {
    response = await fetchWithNetworkRetry('/api/auth/me', { credentials: 'same-origin' }, {
      policy: AUTH_STATE_READ_RETRY,
      retryDelayMs,
      fetchImpl,
    });
  } catch (error) {
    if (!isKnownCrawlerUserAgent(userAgent)) {
      track('auth_state_fetch_failed', {
        reason: 'network_error',
        online_state: onlineState(),
        error_blob: errorBlob('network_error', error),
      });
    }
    onOutcome?.('failed');
    return { status: 'failed', reason: 'network_error' };
  }

  if (!response.ok) {
    track('auth_state_fetch_failed', {
      reason: 'http_error',
      http_status: String(response.status),
      online_state: onlineState(),
      error_blob: errorBlob('http_error', new Error(`Auth state failed: ${response.status}`), response.status),
    });
    onOutcome?.('failed');
    return { status: 'failed', reason: 'http_error' };
  }

  try {
    const payload = await readAccountResponse(response);
    if (isCurrent()) {
      updateBrowserProductAccess(payload);
      rememberOfflineAuthUser(payload.user);
      const internalAnalyticsExcluded = Boolean(
        payload.user
        && isObject(payload.user)
        && 'analytics_excluded' in payload.user
        && payload.user.analytics_excluded === true,
      );
      setInternalAnalyticsExclusion(internalAnalyticsExcluded, analyticsStorage);
      completeSignin(track, internalAnalyticsExcluded, payload.user !== null);
      if (payload.user && !internalAnalyticsExcluded) void refreshCustomerAnalytics();
    }
    onOutcome?.('completed');
    if (payload.user === null) return { status: 'signed_out' };
    // SAFETY: The account endpoint supplies the caller's user contract; the response envelope is checked above.
    return { status: 'signed_in', user: payload.user as User };
  } catch (error) {
    const reason = error instanceof SyntaxError ? 'invalid_response' : 'response_read_error';
    const contentType = responseContentType(response);
    track('auth_state_fetch_failed', {
      reason,
      http_status: String(response.status),
      content_type: contentType,
      online_state: onlineState(),
      error_blob: errorBlob(reason, error, response.status, contentType),
    });
    onOutcome?.('failed');
    return { status: 'failed', reason };
  }
}

/** Own account-read ordering; callers only publish a current account result. */
export function createAuthStateController<User>(deps: {
  load(isCurrent: () => boolean): Promise<AuthStateResult<User>>;
  publish(user: User | null): void;
  settled(): void;
}) {
  type AccountRead = { status: 'idle' } | { status: 'loading'; owner: symbol; completion: Promise<void> };
  let state: AccountRead = { status: 'idle' };
  function refresh({ force = false } = {}): Promise<void> {
    if (state.status === 'loading' && !force) return state.completion;
    const owner = Symbol('account-read');
    const isCurrent = () => state.status === 'loading' && state.owner === owner;
    const completion = Promise.resolve().then(async () => {
      try {
        const result = await deps.load(isCurrent);
        if (isCurrent() && result.status !== 'failed') deps.publish(result.status === 'signed_in' ? result.user : null);
      } finally {
        if (isCurrent()) { state = { status: 'idle' }; deps.settled(); }
      }
    });
    state = { status: 'loading', owner, completion };
    return completion;
  }
  return { refresh };
}
